import { Container } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { t, getCurrentLanguage } from '@/utils/i18n'
import { updateSEO, addStructuredData } from '@/utils/seo'
import { getBlogPosts, getBlogPost } from '@/data/blog'
import type { LocalizedBlogPost } from '@/types/blog'

const SITE_ORIGIN = 'https://gigaette.com'

interface BlogPostPageProps {
  slug: string
}

export function BlogPostPage({ slug }: BlogPostPageProps): HTMLElement {
  const main = document.createElement('div')
  const post = getBlogPost(slug)

  if (!post) {
    return createNotFound()
  }

  // Enrich SEO with the article's OG image + article type + structured data.
  // The router already set the title/description from the route registration;
  // here we add the image, og:type=article, and JSON-LD.
  const lang = getCurrentLanguage()
  // Share card, not the on-page cover: scripts/generate-og.mjs emits a 1200x630
  // JPEG per post (prebuild), because the raw `ogImage` is a .webp at an
  // arbitrary aspect ratio — and WebP og:image is not reliably rendered by
  // KakaoTalk or LinkedIn. The webp stays in use for the listing thumbnail.
  const absImage = post.ogImage ? `${SITE_ORIGIN}/og/blog-${post.slug}.jpg` : undefined
  updateSEO({
    // seoTitle is the shorter <title>-tag variant when the editorial headline
    // would blow the 60-char budget; the on-page H1 still uses post.title.
    title: `${post.seoTitle ?? post.title} - GIGAette`,
    description: post.description,
    path: `/blog/${post.slug}`,
    lang,
    image: absImage,
    type: 'article',
  })
  injectStructuredData(post, lang, absImage)

  main.appendChild(createHero(post))
  main.appendChild(createBody(post))
  if (post.faq.length > 0) main.appendChild(createFaq(post))
  main.appendChild(createNavigation(slug))

  return main
}

function createHero(post: LocalizedBlogPost): HTMLElement {
  const section = document.createElement('section')
  section.className = 'pt-32 pb-16 bg-gradient-to-b from-slate-900 to-slate-800 text-white'

  const container = Container({
    children: (() => {
      const content = document.createElement('div')
      content.className = 'max-w-4xl mx-auto'

      // Back link
      const back = document.createElement('a')
      back.href = '/blog'
      back.className =
        'inline-flex items-center text-slate-400 hover:text-white mb-6 transition-colors'
      back.innerHTML = `<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg> ${t('blogPage.detail.backToBlog')}`
      back.addEventListener('click', (e) => {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('navigate', { detail: '/blog' }))
      })

      // Meta row
      const meta = document.createElement('div')
      meta.className = 'flex flex-wrap items-center gap-x-3 gap-y-2 mb-6 text-sm text-slate-400'
      const datePart = document.createElement('span')
      datePart.textContent = formatDate(post.date)
      const dot = document.createElement('span')
      dot.textContent = '·'
      const reading = document.createElement('span')
      reading.textContent = `${post.readingMinutes} ${t('blogPage.minRead')}`
      meta.appendChild(datePart)
      meta.appendChild(dot)
      meta.appendChild(reading)

      // Title
      const title = document.createElement('h1')
      title.className = 'text-4xl md:text-5xl font-semibold mb-6 pb-1'
      title.style.background = 'linear-gradient(to right, #ffffff, #cbd5e1)'
      title.style.webkitBackgroundClip = 'text'
      title.style.backgroundClip = 'text'
      title.style.webkitTextFillColor = 'transparent'
      title.textContent = post.title

      // Description
      const desc = document.createElement('p')
      desc.className = 'text-xl text-slate-300 leading-relaxed'
      desc.textContent = post.description

      content.appendChild(back)
      content.appendChild(meta)
      content.appendChild(title)
      content.appendChild(desc)

      // Tags
      if (post.tags.length > 0) {
        const tagRow = document.createElement('div')
        tagRow.className = 'flex flex-wrap gap-2 mt-6'
        post.tags.forEach((tag) => {
          const chip = document.createElement('span')
          chip.className =
            'px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-slate-200'
          chip.textContent = tag
          tagRow.appendChild(chip)
        })
        content.appendChild(tagRow)
      }

      return content
    })(),
  })

  section.appendChild(container)
  return section
}

function createBody(post: LocalizedBlogPost): HTMLElement {
  const section = document.createElement('section')
  section.className = 'py-16 bg-white'

  const container = Container({
    children: (() => {
      const wrap = document.createElement('div')
      wrap.className = 'max-w-3xl mx-auto'

      const article = document.createElement('article')
      article.className =
        'prose prose-slate max-w-none prose-headings:font-semibold prose-headings:text-slate-900 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-2xl prose-table:text-sm'
      // Trusted, build-time-rendered HTML (authored by team + AI via CMS).
      article.innerHTML = post.html

      wrap.appendChild(article)
      wrap.appendChild(createShare(post))

      return wrap
    })(),
  })

  section.appendChild(container)
  return section
}

function createFaq(post: LocalizedBlogPost): HTMLElement {
  const section = document.createElement('section')
  section.className = 'py-16 bg-slate-50'

  const container = Container({
    children: (() => {
      const wrap = document.createElement('div')
      wrap.className = 'max-w-3xl mx-auto'

      const heading = document.createElement('h2')
      heading.className = 'text-2xl md:text-3xl font-semibold text-slate-900 mb-8'
      heading.textContent = t('blogPage.detail.faqTitle')
      wrap.appendChild(heading)

      post.faq.forEach((item) => {
        const details = document.createElement('details')
        details.className = 'group border-b border-slate-200 py-4'

        const summary = document.createElement('summary')
        summary.className =
          'flex justify-between items-center cursor-pointer font-semibold text-slate-900 list-none'
        summary.innerHTML = `<span>${escapeHtml(item.q)}</span><svg class="w-5 h-5 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>`

        const answer = document.createElement('p')
        answer.className = 'mt-3 text-slate-600 leading-relaxed'
        answer.textContent = item.a

        details.appendChild(summary)
        details.appendChild(answer)
        wrap.appendChild(details)
      })

      return wrap
    })(),
  })

  section.appendChild(container)
  return section
}

function createShare(post: LocalizedBlogPost): HTMLElement {
  const shareSection = document.createElement('div')
  shareSection.className = 'mt-12 pt-8 border-t border-slate-200'

  const label = document.createElement('p')
  label.className = 'text-sm text-slate-500 mb-4'
  label.textContent = t('blogPage.detail.shareArticle')

  const buttons = document.createElement('div')
  buttons.className = 'flex gap-4'

  const linkedin = document.createElement('button')
  linkedin.className =
    'w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white transition-colors'
  linkedin.innerHTML =
    '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>'
  linkedin.addEventListener('click', () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
      '_blank'
    )
  })

  const x = document.createElement('button')
  x.className =
    'w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-sky-500 hover:text-white transition-colors'
  x.innerHTML =
    '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>'
  x.addEventListener('click', () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`,
      '_blank'
    )
  })

  const copy = document.createElement('button')
  copy.className =
    'w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-600 hover:text-white transition-colors'
  const copyIcon =
    '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>'
  copy.innerHTML = copyIcon
  copy.addEventListener('click', () => {
    navigator.clipboard.writeText(window.location.href)
    copy.innerHTML =
      '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
    setTimeout(() => (copy.innerHTML = copyIcon), 2000)
  })

  buttons.appendChild(linkedin)
  buttons.appendChild(x)
  buttons.appendChild(copy)
  shareSection.appendChild(label)
  shareSection.appendChild(buttons)
  return shareSection
}

function createNavigation(slug: string): HTMLElement {
  const section = document.createElement('section')
  section.className = 'py-12 bg-slate-50'

  const posts = getBlogPosts()
  const idx = posts.findIndex((p) => p.slug === slug)
  const prev = idx > 0 ? posts[idx - 1] : null
  const next = idx >= 0 && idx < posts.length - 1 ? posts[idx + 1] : null

  const container = Container({
    children: (() => {
      const content = document.createElement('div')
      content.className = 'max-w-3xl mx-auto'

      const navRow = document.createElement('div')
      navRow.className = 'flex justify-between items-stretch gap-4 mb-8'
      navRow.appendChild(prev ? createNavLink(prev, 'prev') : spacer())
      navRow.appendChild(next ? createNavLink(next, 'next') : spacer())
      content.appendChild(navRow)

      const btnWrap = document.createElement('div')
      btnWrap.className = 'text-center'
      btnWrap.appendChild(
        Button({ children: t('blogPage.detail.viewAll'), variant: 'secondary', href: '/blog' })
      )
      content.appendChild(btnWrap)

      return content
    })(),
  })

  section.appendChild(container)
  return section
}

function spacer(): HTMLElement {
  const d = document.createElement('div')
  d.className = 'flex-1'
  return d
}

function createNavLink(post: LocalizedBlogPost, direction: 'prev' | 'next'): HTMLElement {
  const link = document.createElement('a')
  link.href = `/blog/${post.slug}`
  link.className = `flex-1 p-4 bg-white rounded-xl hover:shadow-md transition-all duration-300 group ${direction === 'next' ? 'text-right' : ''}`
  link.addEventListener('click', (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: `/blog/${post.slug}` }))
  })

  const label = document.createElement('p')
  label.className = 'text-sm text-slate-500 mb-1 flex items-center gap-1'
  if (direction === 'prev') {
    label.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg> ${t('blogPage.detail.previous')}`
  } else {
    label.className += ' justify-end'
    label.innerHTML = `${t('blogPage.detail.next')} <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>`
  }

  const title = document.createElement('p')
  title.className =
    'font-semibold text-slate-900 group-hover:text-orange-600 transition-colors line-clamp-2'
  title.textContent = post.title

  link.appendChild(label)
  link.appendChild(title)
  return link
}

function createNotFound(): HTMLElement {
  const main = document.createElement('div')
  main.className = 'min-h-screen flex items-center justify-center bg-slate-50'

  const content = document.createElement('div')
  content.className = 'text-center'

  const title = document.createElement('h1')
  title.className = 'text-4xl font-bold text-slate-900 mb-4'
  title.textContent = t('blogPage.detail.notFound.title')

  const text = document.createElement('p')
  text.className = 'text-slate-600 mb-8'
  text.textContent = t('blogPage.detail.notFound.subtitle')

  content.appendChild(title)
  content.appendChild(text)
  content.appendChild(
    Button({ children: t('blogPage.detail.backToBlog'), variant: 'primary', href: '/blog' })
  )
  main.appendChild(content)
  return main
}

function injectStructuredData(
  post: LocalizedBlogPost,
  lang: 'en' | 'ko',
  absImage: string | undefined
): void {
  const url = `${SITE_ORIGIN}${lang === 'ko' ? '/ko' : ''}/blog/${post.slug}/`

  addStructuredData(
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: post.updated || post.date,
      ...(absImage ? { image: absImage } : {}),
      author: { '@type': 'Organization', name: 'GIGAette' },
      publisher: {
        '@type': 'Organization',
        name: 'GIGAette',
        logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/logo.png` },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      inLanguage: lang,
    },
    'blog-article'
  )

  if (post.faq.length > 0) {
    addStructuredData(
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      'blog-faq'
    )
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function formatDate(dateStr: string): string {
  const lang = getCurrentLanguage()
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
